/**
 * 팝업 상단 배너 (TOP_BANNER).
 *
 * dismiss 정책:
 *  - X 닫기 → 세션 dismiss
 *  - "오늘 하루 보지 않기" 링크 → 24h dismiss (공간 제약으로 드롭다운 대신 단일 링크)
 *
 * 영구지침: docs/develop/popup/popup-spec.md §9.4
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, X } from 'lucide-react'
import type { PopupResponse } from '@/features/popup/types'
import { recordClick, recordDismiss, recordImpression } from '@/features/popup/api'
import { markDismissedFor } from '@/lib/popup/dismissStorage'
import { firstMarkdownImage, stripMarkdown } from './PopupMarkdown'

interface Props {
  popup: PopupResponse
}

export default function TopBanner({ popup }: Props) {
  const navigate = useNavigate()
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    void recordImpression(popup.id)
  }, [popup.id])

  if (!visible) return null

  const handleClick = () => {
    void recordClick(popup.id)
    if (popup.ctaUrl) navigate(popup.ctaUrl)
  }

  /** X 닫기 → 세션 dismiss */
  const handleClose = () => {
    if (!popup.allowDismiss) return
    void recordDismiss(popup.id)
    // 그냥 닫기는 저장하지 않는다 → 새로고침·다음 방문 시 다시 노출. 기간 억제는 별도 버튼에서만.
    setVisible(false)
  }

  /** "오늘 하루 보지 않기" → 24h dismiss */
  const handleDismissToday = () => {
    void recordDismiss(popup.id)
    markDismissedFor(popup.id, 24)
    setVisible(false)
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-30 bg-blue-50 px-4 py-2 text-sm text-blue-900"
    >
      <div className="flex items-center justify-center gap-3">
        {(() => {
          // 배너 썸네일: 대표 이미지(imageUrl) 우선, 없으면 본문 첫 이미지. 둘 다 없으면 Bell 아이콘.
          const thumb = popup.imageUrl || firstMarkdownImage(popup.body ?? '')
          return thumb ? (
            <img
              src={thumb}
              alt=""
              className="h-6 w-6 flex-shrink-0 rounded object-cover"
              onError={(e) => {
                ;(e.currentTarget as HTMLImageElement).style.display = 'none'
              }}
            />
          ) : (
            <Bell className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          )
        })()}
        <p className="flex-1 truncate text-center font-medium">
          <span className="font-semibold">{popup.title}</span>
          {(() => {
            const text = stripMarkdown(popup.body ?? '')
            return text ? <span className="ml-2 text-blue-700">· {text}</span> : null
          })()}
        </p>
        {popup.ctaLabel && popup.ctaUrl && (
          <button
            type="button"
            onClick={handleClick}
            className="flex-shrink-0 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            {popup.ctaLabel}
          </button>
        )}
        {popup.allowDismiss && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="배너 닫기"
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-blue-700 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {popup.allowDismiss && (
        <div className="mt-0.5 text-center">
          <button
            type="button"
            onClick={handleDismissToday}
            className="text-[11px] text-blue-600 underline underline-offset-2 hover:text-blue-800"
          >
            오늘 하루 보지 않기
          </button>
        </div>
      )}
    </div>
  )
}
